import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import AppNav from "../components/AppNav";
import { useDarkMode } from "../hooks/useDarkMode";
import { BACKEND_URL } from "../lib/backend";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum } from "./theme";
import { DeltaBadge, SourceChip, SkeletonCard } from "./components";
import { savePulseNext } from "./PulseGate";
import { LangToggle } from "./PulseLayout";
import { ForecastKpiCard } from "./forecast";
import "./pulse.css";

const PREVIEW_BG = "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// CTA must NOT clobber a deep-link next saved by PulseGate
const ensureNext = (path) => {
  try { if (!localStorage.getItem("pulse_next")) savePulseNext(path); } catch { /* noop */ }
};

/** Public teaser — the section's only SEO/AEO surface. Real headline numbers
 *  (rebuilt at every sync), blurred previews of gated screens, a rendered
 *  Ask-the-Data sample with citations, bilingual CTA. */
export default function PulseTeaser() {
  const [dm] = useDarkMode();
  const { t, lang, withLang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    axios.get(`${BACKEND_URL}/api/pulse/teaser`)
      .then((r) => { if (on) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, []);

  // SEO: title + meta description in the active locale
  useEffect(() => {
    const prev = document.title;
    document.title = lang === "it"
      ? "Pulse by TourismIntel — Intelligence sui dati del turismo"
      : "Pulse by TourismIntel — Tourism Data Intelligence";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", t("teaser_sub"));
    return () => {
      document.title = prev;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [lang, t]);

  // schema.org Dataset markup (AEO). dateModified = real sync time, never "now".
  useEffect(() => {
    if (!data?.built_at) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "pulse-dataset-jsonld";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: "Pulse by TourismIntel — Tourism Statistics Warehouse",
      description: "Official tourism statistics from OECD, Eurostat and UN Tourism (via Our World in Data): international arrivals, receipts, monthly nights, platform-economy guest nights.",
      url: "https://tourismintel.ai/pulse",
      creator: { "@type": "Organization", name: "TourismIntel", url: "https://tourismintel.ai" },
      isBasedOn: ["https://sdmx.oecd.org", "https://ec.europa.eu/eurostat", "https://ourworldindata.org"],
      license: "https://tourismintel.ai/data-license.html",
      dateModified: data.built_at,
      distribution: [{
        "@type": "DataDownload",
        encodingFormat: "text/markdown",
        contentUrl: "https://tourismintel.ai/api/pulse/teaser.md",
      }],
    });
    document.head.appendChild(s);
    return () => { try { s.remove(); } catch { /* noop */ } };
  }, [data]);

  const kpis = data?.kpis || [];
  const stats = data?.stats;
  const fks = (data?.forecast_kpis || (data?.forecast_kpi ? [data.forecast_kpi] : [])).filter((k) => k.confidence === "alta");
  const nCards = kpis.length + fks.length;
  const gridCols = nCards % 3 === 0 ? "lg:grid-cols-3" : (nCards === 5 ? "lg:grid-cols-5" : "lg:grid-cols-4");

  const previews = [
    { key: "world", title: t("teaser_preview_world"), desc: t("teaser_preview_world_desc") },
    { key: "italy", title: t("teaser_preview_italy"), desc: t("teaser_preview_italy_desc") },
    { key: "seasonality", title: t("teaser_preview_seasonality"), desc: t("teaser_preview_seasonality_desc") },
    { key: "rankings", title: t("teaser_preview_rankings"), desc: t("teaser_preview_rankings_desc") },
    { key: "cities", title: t("teaser_preview_cities"), desc: t("teaser_preview_cities_desc") },
  ];

  return (
    <div className="pulse-root min-h-screen" style={{ background: T.bg, color: T.text }} data-testid="pulse-teaser">
      <AppNav darkMode={dm} />
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6">
        {/* Hero */}
        <section className="pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="flex items-center justify-between gap-4 mb-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: T.accent }}>
              {t("teaser_eyebrow")}
            </p>
            <LangToggle dm={dm} />
          </div>
          <h1 className="pulse-serif text-4xl sm:text-5xl lg:text-6xl font-medium max-w-3xl leading-tight" data-testid="teaser-title">
            {t("teaser_title")}
          </h1>
          <p className="text-base md:text-lg mt-5 max-w-2xl leading-relaxed" style={{ color: T.muted }}>
            {t("teaser_sub")}
          </p>
          <div className="flex items-center gap-4 mt-8 flex-wrap">
            <Link to="/auth" onClick={() => ensureNext(withLang("/pulse"))} data-testid="teaser-cta-signup"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold"
              style={{ background: T.brand, color: "#fff", borderRadius: 2 }}>
              {t("teaser_cta")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/auth" onClick={() => ensureNext(withLang("/pulse"))} data-testid="teaser-cta-login"
              className="text-sm font-semibold underline underline-offset-4" style={{ color: T.muted }}>
              {t("teaser_login")}
            </Link>
          </div>
          <p className="text-xs mt-3" style={{ color: T.muted }}>{t("teaser_cta_sub")}</p>
        </section>

        {/* Real headline numbers */}
        <section className="pb-16" aria-labelledby="teaser-headline">
          <h2 id="teaser-headline" className="pulse-serif text-lg font-semibold mb-4">{t("teaser_headline")}</h2>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols}`} style={{ gap: 1, background: T.border, border: `1px solid ${T.border}` }}>
            {loading && [0, 1, 2, 3].map((i) => <SkeletonCard key={i} h={150} dm={dm} />)}
            {!loading && kpis.map((k) => (
              <div key={k.id} className="p-5 flex flex-col gap-2" style={{ background: T.surface }} data-testid={`teaser-kpi-${k.id}`}>
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: T.muted }}>
                  {lang === "it" ? k.label_it : k.label_en}
                </span>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="pulse-num text-3xl md:text-4xl font-semibold">{fmtCompact(k.value, lang)}</span>
                  <DeltaBadge pct={k.yoy_pct} dm={dm} />
                </div>
                <SourceChip source={k.source_org} dataset={k.dataset_code}
                  period={(lang === "it" ? k.period_it : k.period_en) || k.period}
                  experimental={k.data_type === "experimental"} dm={dm} />
              </div>
            ))}
            {!loading && fks.map((fk) => <ForecastKpiCard key={fk.id} fk={fk} dm={dm} />)}
          </div>
          {stats && (
            <p className="text-xs mt-3" style={{ color: T.muted }} data-testid="teaser-stats">
              {t("teaser_stats", { obs: fmtNum(stats.observations, lang), sources: stats.sources, economies: stats.economies })}
            </p>
          )}
        </section>

        {/* Blurred previews of gated screens */}
        <section className="pb-16">
          <h2 className="pulse-serif text-lg font-semibold mb-4">{t("teaser_preview_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {previews.map((p) => (
              <Link key={p.key} to="/auth" onClick={() => ensureNext(withLang("/pulse"))}
                data-testid={`teaser-preview-${p.key}`}
                className="relative overflow-hidden block group" style={{ border: `1px solid ${T.border}` }}>
                <div className="h-40 w-full" style={{
                  backgroundImage: `url(${PREVIEW_BG})`, backgroundSize: "cover", backgroundPosition: "center",
                  filter: "blur(7px) saturate(0.8)", transform: "scale(1.1)" }} aria-hidden="true" />
                <div className="absolute inset-0 flex flex-col justify-end p-4"
                  style={{ background: "linear-gradient(180deg, rgba(15,17,21,0.15) 0%, rgba(15,17,21,0.88) 100%)" }}>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#FBBF24" }}>
                    <Lock className="w-3 h-3" aria-hidden="true" /> {t("teaser_locked")}
                  </span>
                  <span className="pulse-serif text-lg font-semibold text-white">{p.title}</span>
                  <span className="text-xs text-gray-300 leading-snug mt-1">{p.desc}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Ask the Data — one fully rendered sample Q&A with citations */}
        <section className="pb-24">
          <div className="p-6 md:p-8 max-w-3xl" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="teaser-ask-sample">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Sparkles className="w-4 h-4" style={{ color: T.accent }} aria-hidden="true" />
              <span className="pulse-serif text-lg font-semibold">{t("teaser_ask_title")}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5"
                style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>{t("teaser_ask_soon")}</span>
            </div>
            <p className="text-sm font-semibold mb-3" style={{ color: T.text }}>“{t("teaser_ask_q")}”</p>
            <p className="text-sm leading-relaxed mb-3" style={{ color: T.muted }}>{t("teaser_ask_a")}</p>
            {/* citation rule (owner, 2026-08-28): human-readable chips only — dataset codes live in the tooltip */}
            <div className="flex flex-col gap-1.5" data-testid="teaser-ask-citations">
              <SourceChip source="OECD" dataset="DSD_TOURISM_INTER@DF_INBOUND" period="2024" dm={dm} testid="teaser-ask-cite-oecd" />
              <SourceChip source="EUROSTAT" dataset="tour_occ_nim" period="2026-05" dm={dm} testid="teaser-ask-cite-eurostat" />
            </div>
            <p className="text-[11px] mt-4" style={{ color: T.muted }}>{t("teaser_ask_note")}</p>
          </div>
        </section>
      </main>
      <footer className="max-w-screen-xl mx-auto px-4 sm:px-6 pb-10">
        <p className="text-[11px]" style={{ color: T.muted }}>{t("footer_attribution")}</p>
      </footer>
    </div>
  );
}
